/* global bootstrap */
import { reactive, computed } from 'vue'
import BpmOsgpoModule from '@/helpers/Modules/Product/BpmOsgpoModule'
import { normalizeIdentity } from '@/helpers/Modules/Product/bpm/processIdentity'

export function useBpmParticipants ({ policy, notify, sharedApi }) {
  const clientModalState = reactive({
    new: true,
    key: null,
    data: null,
    processId: null,
    hash: null,
    clientType: 0,
  })
  const vehicleModalState = reactive({
    new: false,
    key: null,
    data: null,
    processId: null,
    hash: null,
  })
  const processModalState = reactive({
    id: null,
  })

  function getClientIdentity (client) {
    return client?.client?.iin ?? client?.document?.number ?? client?.inst_id ?? null
  }

  function isEntrepreneurClient (client) {
    return !!(client?.entrepreneur && client.entrepreneur.name)
  }

  function isSameVehicle (firstVehicle, secondVehicle) {
    if (!firstVehicle || !secondVehicle) {
      return false
    }
    const normalize = (value) => {
      if (value === undefined || value === null) return null
      const prepared = String(value).trim().toUpperCase()
      return prepared || null
    }
    const firstInstId = normalize(firstVehicle?.inst_id)
    const secondInstId = normalize(secondVehicle?.inst_id)
    if (firstInstId && secondInstId && firstInstId === secondInstId) {
      return true
    }
    const firstVin = normalize(firstVehicle?.vin)
    const secondVin = normalize(secondVehicle?.vin)
    if (firstVin && secondVin && firstVin === secondVin) {
      return true
    }
    const firstNumber = normalize(firstVehicle?.registration?.number ?? firstVehicle?.number)
    const secondNumber = normalize(secondVehicle?.registration?.number ?? secondVehicle?.number)
    const firstCertificate = normalize(firstVehicle?.registration?.certificate ?? firstVehicle?.certificate)
    const secondCertificate = normalize(secondVehicle?.registration?.certificate ?? secondVehicle?.certificate)
    if (firstNumber && secondNumber && firstNumber === secondNumber
        && firstCertificate && secondCertificate && firstCertificate === secondCertificate) {
      return true
    }
    return false
  }

  function isSameClient (firstClient, secondClient) {
    if (!firstClient || !secondClient) {
      return false
    }
    return JSON.stringify([
      firstClient?.client?.iin ?? null,
      firstClient?.client?.title ?? null,
      firstClient?.document?.issued ?? null,
      firstClient?.document?.number ?? null,
    ]) === JSON.stringify([
      secondClient?.client?.iin ?? null,
      secondClient?.client?.title ?? null,
      secondClient?.document?.issued ?? null,
      secondClient?.document?.number ?? null,
    ])
  }

  function syncAddedLists () {
    if (!policy) {
      return
    }
    const clientSet = new Set()
    const carSet = new Set()

    const pushClientIdentity = (value) => {
      const normalized = normalizeIdentity(value)
      if (normalized) {
        clientSet.add(normalized)
      }
    }
    const pushCarIdentity = (value) => {
      const normalized = normalizeIdentity(value)
      if (normalized) {
        carSet.add(normalized)
      }
    }
    const pushClientEntity = (client) => {
      if (!client || isEntrepreneurClient(client)) {
        return
      }
      pushClientIdentity(client?.client?.iin)
      pushClientIdentity(client?.client?.phone)
      pushClientIdentity(client?.licence?.number)
    }
    const pushVehicleEntity = (vehicle) => {
      if (!vehicle) {
        return
      }
      pushCarIdentity(vehicle?.vin)
      pushCarIdentity(vehicle?.registration?.number ?? vehicle?.number)
      pushCarIdentity(vehicle?.registration?.certificate ?? vehicle?.certificate)
    }
    const pushClientProcess = (process) => {
      const search = process?.meta?.search ?? process?.init_data ?? {}
      pushClientIdentity(search?.iin)
      pushClientIdentity(search?.nomer_vu)
      pushClientIdentity(search?.phone)
    }
    const pushVehicleProcess = (process) => {
      const search = process?.meta?.search ?? process?.init_data ?? {}
      pushCarIdentity(search?.vin)
      pushCarIdentity(search?.number)
      pushCarIdentity(search?.certificate)
    }

    ;(policy.insureds || []).forEach(pushClientEntity)
    ;(policy.vehicle || []).forEach(pushVehicleEntity)
    ;(policy.business_process || []).forEach(process => {
      if (![1, 2, 3].includes(process?.type) || !BpmOsgpoModule.BusinessProcess.isActive(process)) {
        return
      }
      if (process.type === 3) {
        pushVehicleProcess(process)
        return
      }
      pushClientProcess(process)
    })

    policy.clientAddedList = Array.from(clientSet)
    policy.carAddedList = Array.from(carSet)
  }

  function filterModalAddedList (list, processId, initData, collectIdentityValues) {
    if (!processId) {
      return list
    }
    const process = Array.isArray(policy?.business_process)
      ? policy.business_process.find(item => item?.id === processId)
      : null
    const processSearch = process?.meta?.search ?? process?.init_data ?? {}
    const editingIdentities = new Set(
      collectIdentityValues(initData || {}, processSearch)
        .map(value => normalizeIdentity(value))
        .filter(Boolean),
    )
    if (!editingIdentities.size) {
      return list
    }
    return list.filter(item => {
      const normalized = normalizeIdentity(item)
      return !normalized || !editingIdentities.has(normalized)
    })
  }

  function enrichInsurerBeforeIsDrive () {
    if (!policy.insurer) {
      return
    }
    const insurerAsInsured = policy.insureds.find(insured => isSameClient(insured, policy.insurer))
    if (insurerAsInsured) {
      if (!policy.insurer.licence && insurerAsInsured.licence) {
        policy.insurer.licence = structuredClone(insurerAsInsured.licence)
      }
      if (!policy.insurer.driver && insurerAsInsured.driver) {
        policy.insurer.driver = structuredClone(insurerAsInsured.driver)
      }
    }
  }

  function openSwitchInsuredModal () {
    if (!policy.insurer?.licence) {
      notify.error({ message: 'У страхователя нет водительского удостоверения' })
      return
    }
    setTimeout(() => { new bootstrap.Modal('#customerBpmSwitchModal', {}).show() }, 10)
  }

  function switchInsurer (insured) {
    const oldInsurer = policy.insurer
    if (policy.isDrive) {
      policy.insurer = insured
      const insuredIndex = policy.insureds.findIndex(item => isSameClient(item, insured))
      if (insuredIndex !== -1) {
        policy.insureds.splice(insuredIndex, 1)
      }
      policy.insureds.unshift(insured)
    } else {
      const insuredIdx = policy.insureds.findIndex(item => isSameClient(item, insured))
      if (insuredIdx !== -1) {
        policy.insureds.splice(insuredIdx, 1)
      }
      policy.insureds.push(policy.insurer)
      policy.insurer = insured
    }
    policy.business_process.forEach(process => {
      if (process?.type !== 1 && process?.type !== 2) return
      if (sharedApi.processMatchesClient(process, oldInsurer)) process.type = 2
      else if (sharedApi.processMatchesClient(process, insured)) process.type = 1
    })
  }

  function touchModalHash (state) {
    state.hash = Math.floor(Math.random() * 10) + 1
  }

  function showBpmModal (selector) {
    setTimeout(() => { new bootstrap.Modal(selector, {}).show() }, 10)
  }

  function openClientModal (clientType, data = null, key = null) {
    clientModalState.clientType = clientType
    clientModalState.new = !data && key == null
    clientModalState.key = key
    clientModalState.processId = null

    if (data && (data.client || data.document)) {
      delete clientModalState.step
      clientModalState.data = structuredClone(data)
    } else {
      clientModalState.step = 0
      clientModalState.data = data
    }

    touchModalHash(clientModalState)
    showBpmModal('#customerBpmModal')
  }

  function deleteInsurer () {
    if (policy.insurer) {
      sharedApi.removeBusinessProcessByEntity(policy.insurer)
    }
    policy.insurer = null
  }

  function searchClient (val, status = 1) {
    if (Object.prototype.hasOwnProperty.call(val, 'processId') && val.processId) {
      const process = policy.business_process.find(item => item.id === val.processId)
      if (process) {
        process.init_data = val.search
        sharedApi.restartProcess(process.id)
        return
      }
    }
    if (val.clientType === 1) {
      const insurerProcess = sharedApi.addProcess(1, val.search, val, status)
      if (val.search.isDrive && insurerProcess && sharedApi.getProcessNomerVu({ init_data: val.search })) {
        sharedApi.addMirrorProcess(insurerProcess, 2, val.search, { ...(val || {}), clientType: 2 })
      }
    } else {
      sharedApi.addProcess(2, val.search, val, status)
    }
  }

  function editRejectedClient (clientType, id) {
    clientModalState.clientType = clientType
    clientModalState.new = false
    clientModalState.key = null
    clientModalState.data = policy.business_process.find(item => item.id === id).init_data
    clientModalState.step = 0
    clientModalState.processId = id
    touchModalHash(clientModalState)
    showBpmModal('#customerBpmModal')
  }

  function deleteInsured (key) {
    if (policy.isDrive && (policy.insureds[key].client.iin ? policy.insureds[key].client.iin === policy.insurer.client.iin : policy.insureds[key].document?.number === policy.insurer.document?.number) &&
        !((policy.insurer.entrepreneur && policy.insurer.entrepreneur.name) || (policy.insureds[key].entrepreneur && policy.insureds[key].entrepreneur.name))) {
      policy.isDrive = false
    } else {
      const insured = policy.insureds[key]
      if (policy.insureds[key].client.isInsurer) {
        policy.insureds.splice(key, 1)
        if (policy.insureds.length !== 0) {
          policy.insureds[0].client.isInsurer = true
        }
      } else {
        policy.insureds.splice(key, 1)
      }
      if (insured) {
        sharedApi.removeBusinessProcessByEntity(insured)
      }
    }
  }

  function searchVehicle (val) {
    if (Object.prototype.hasOwnProperty.call(val, 'processId') && val.processId) {
      const process = policy.business_process.find(item => item.id === val.processId)
      if (process) {
        process.init_data = val.search
        sharedApi.restartProcess(process.id)
      }
    } else {
      sharedApi.addProcess(3, val.search, val)
    }
  }

  function setChangedVehicle (key, isNew = false) {
    vehicleModalState.new = isNew
    vehicleModalState.key = key
    vehicleModalState.processId = null

    if (isNew || key == null) {
      vehicleModalState.step = 0
      vehicleModalState.data = null
    } else {
      delete vehicleModalState.step
      vehicleModalState.data = structuredClone(policy.vehicle[key])
    }

    touchModalHash(vehicleModalState)
    showBpmModal('#vehicleBpmModal')
  }

  function deleteVehicle (key) {
    const vehicle = policy.vehicle[key]
    policy.vehicle.splice(key, 1)
    if (vehicle) {
      sharedApi.removeBusinessProcessByEntity(vehicle)
    }
  }

  function editRejectedVehicle (id) {
    vehicleModalState.new = false
    vehicleModalState.key = null
    vehicleModalState.data = policy.business_process.find(item => item.id === id).init_data
    vehicleModalState.step = 0
    vehicleModalState.processId = id
    touchModalHash(vehicleModalState)
    showBpmModal('#vehicleBpmModal')
  }

  const clientModalExcludedClients = computed(() => {
    const list = Array.isArray(policy?.clientAddedList) ? policy.clientAddedList : []
    return filterModalAddedList(list, clientModalState?.processId, clientModalState?.data, (init, search) => [
      init?.iin,
      init?.nomer_vu,
      init?.phone,
      search?.iin,
      search?.nomer_vu,
      search?.phone,
    ])
  })

  const vehicleModalExcludedCars = computed(() => {
    const list = Array.isArray(policy?.carAddedList) ? policy.carAddedList : []
    return filterModalAddedList(list, vehicleModalState?.processId, vehicleModalState?.data, (init, search) => [
      init?.vin,
      init?.number ?? init?.registration?.number,
      init?.certificate ?? init?.registration?.certificate,
      search?.vin,
      search?.number,
      search?.certificate,
    ])
  })

  return {
    clientModalState,
    vehicleModalState,
    processModalState,
    clientModalExcludedClients,
    vehicleModalExcludedCars,
    getClientIdentity,
    isEntrepreneurClient,
    isSameVehicle,
    isSameClient,
    normalizeIdentity,
    syncAddedLists,
    filterModalAddedList,
    enrichInsurerBeforeIsDrive,
    openSwitchInsuredModal,
    switchInsurer,
    openClientModal,
    deleteInsurer,
    searchClient,
    editRejectedClient,
    deleteInsured,
    searchVehicle,
    setChangedVehicle,
    deleteVehicle,
    editRejectedVehicle,
  }
}
